import { c as _c } from "react/compiler-runtime";
import { useMemberSelectionDisplay } from "@components/common/member-autocomplete/use-member-selection-display";
import { EmailIcon, MemberAutocompleteSelection, grey, SingleSelectAutocomplete, TeamIcon } from "@flat-ui";
import { Box } from "@mui-wrapper";
import { TEAM_DISPLAY_NAME } from "@src/constants/team-constants";
import { FaroCoreApiUtils } from "@stellar/core-api-client";
import { EmailUtils } from "@stellar/utils";
import { DEFAULT_FONT_FAMILY_ITALIC } from "@styles/common-styles";
import { capitalizeFirstLetter } from "@utils/string-utils";
import { getPrettyName } from "@utils/user-utils";
import { useEffect, useMemo, useState } from "react";

/** Re-export type so that we can import SelectedMember from this component.  */

/**
 * Defines the maximum items to be displayed in the autocomplete menu as suggestions.
 * The number should be low enough to fit them all at once in the screen.
 */
const MAX_ITEMS_TO_DISPLAY = 5;

/**
 * Height of the input, which the `MemberAutocompleteSelection` overlay rendered by
 * `renderSelection` fills. It needs to fit an avatar and two lines of text.
 */
const SELECTION_HEIGHT = "65px";

/**
 * Maps a member from the store to an option understood by the autocomplete.
 *
 * @param user The member to map.
 * @returns The option representing the member in the dropdown.
 */
function mapMemberToOption(user, note) {
  if (!FaroCoreApiUtils.Member.isMemberActive(user)) {
    return {
      title: getPrettyName(user),
      subtitle: note,
      value: user.identity,
      avatarIcon: <EmailIcon />
    };
  }
  if (FaroCoreApiUtils.Member.isTeam(user)) {
    return {
      title: getPrettyName(user),
      subtitle: note ? `${capitalizeFirstLetter(TEAM_DISPLAY_NAME)} · ${note}` : capitalizeFirstLetter(TEAM_DISPLAY_NAME),
      value: user.identity,
      avatarIcon: <TeamIcon />,
      avatarStyle: {
        backgroundColor: grey[700]
      }
    };
  }
  return {
    title: getPrettyName(user),
    subtitle: note ? `${user.email} · ${note}` : user.email,
    avatarImage: user.thumbnailUrl,
    value: user.identity
  };
}

/**
 * Maps the currently selected member to the value understood by the autocomplete.
 *
 * @param member The selected member, a typed email, or null.
 * @returns The option to display as selected, or null when nothing is selected.
 */
function mapSelectedMemberToValue(member) {
  if (!member) {
    return null;
  }
  if (typeof member === "string") {
    return {
      title: member,
      value: member
    };
  }
  return {
    title: getPrettyName(member),
    subtitle: FaroCoreApiUtils.Member.isMemberActive(member) ? member.email : undefined,
    value: member.identity
  };
}

/**
 * This component shows an autocomplete selection that let's the user to
 * select one member from a list, and optionally type a valid email.
 * The component is generic enough that can be used for any purpose regarding
 * selecting a single member. For selecting multiple members see the flat-ui
 * `Autocomplete` component (see `useMemberInviteAutocomplete`).
 */
export function MemberAutocomplete(t0) {
  const $ = _c(70);
  const {
    members,
    selectedMember,
    placeholder,
    isDisabled: t1,
    canInviteByEmail: t2,
    shouldShowAllOptions: t3,
    memberNotes,
    onOptionChange
  } = t0;
  const isDisabled = t1 === undefined ? false : t1;
  const canInviteByEmail = t2 === undefined ? true : t2;
  const shouldShowAllOptions = t3 === undefined ? false : t3;
  let t4;
  if ($[0] !== memberNotes || $[1] !== members) {
    t4 = function mapMembersToOptions() {
      return members.map(member => mapMemberToOption(member, memberNotes?.get(member.identity)));
    };
    $[0] = memberNotes;
    $[1] = members;
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  const mapMembersToOptions = t4;
  const [inputValue, setInputValue] = useState("");
  const [currentOptions, setCurrentOptions] = useState(mapMembersToOptions);
  let t5;
  if ($[3] !== selectedMember) {
    t5 = mapSelectedMemberToValue(selectedMember);
    $[3] = selectedMember;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  const selectedValue = t5;
  const selectionDisplay = useMemberSelectionDisplay(selectedMember);
  let t6;
  bb0: {
    if (!canInviteByEmail || !EmailUtils.isValidEmail(inputValue)) {
      t6 = false;
      break bb0;
    }
    let t7;
    if ($[5] !== inputValue) {
      t7 = inputValue.toLowerCase();
      $[5] = inputValue;
      $[6] = t7;
    } else {
      t7 = $[6];
    }
    const inputLower = t7;
    let t8;
    if ($[7] !== inputLower || $[8] !== members) {
      t8 = members?.some(user => user.email.toLowerCase() === inputLower);
      $[7] = inputLower;
      $[8] = members;
      $[9] = t8;
    } else {
      t8 = $[9];
    }
    t6 = !t8;
  }
  const isValidNewEmail = t6;
  let t7;
  let t8;
  if ($[10] !== memberNotes || $[11] !== members) {
    t7 = () => {
      setCurrentOptions(members.map(member_0 => mapMemberToOption(member_0, memberNotes?.get(member_0.identity))));
    };
    t8 = [members, memberNotes];
    $[10] = memberNotes;
    $[11] = members;
    $[12] = t7;
    $[13] = t8;
  } else {
    t7 = $[12];
    t8 = $[13];
  }
  useEffect(t7, t8);
  let t9;
  if ($[14] !== members) {
    t9 = function findMemberByEmail(email) {
      const lowerEmail = email.toLowerCase();
      return members.find(user_0 => user_0.email?.toLowerCase() === lowerEmail);
    };
    $[14] = members;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  const findMemberByEmail = t9;
  let t10;
  if ($[16] !== canInviteByEmail || $[17] !== currentOptions || $[18] !== onOptionChange) {
    t10 = function addUserIfValidEmail(inputText) {
      if (!canInviteByEmail || !EmailUtils.isValidEmail(inputText)) {
        return;
      }
      const newOption = {
        title: inputText,
        value: inputText
      };
      setCurrentOptions([...currentOptions, newOption]);
      onOptionChange(inputText);
    };
    $[16] = canInviteByEmail;
    $[17] = currentOptions;
    $[18] = onOptionChange;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  const addUserIfValidEmail = t10;
  let t11;
  if ($[20] !== members) {
    t11 = function resolveSelection(optionValue) {
      return members.find(user_1 => user_1.identity === optionValue) ?? optionValue;
    };
    $[20] = members;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  const resolveSelection = t11;
  let t12;
  if ($[22] !== onOptionChange || $[23] !== resolveSelection) {
    t12 = function onSelectionChange(newValue) {
      if (newValue && typeof newValue === "object") {
        onOptionChange(resolveSelection(newValue.value));
      } else {
        if (typeof newValue === "string") {
          onOptionChange(newValue);
        } else {
          onOptionChange(null);
        }
      }
    };
    $[22] = onOptionChange;
    $[23] = resolveSelection;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  const onSelectionChange = t12;
  let t13;
  if ($[25] !== addUserIfValidEmail || $[26] !== canInviteByEmail || $[27] !== findMemberByEmail || $[28] !== inputValue || $[29] !== onOptionChange) {
    t13 = function handleKeyPress(event) {
      if (event.key !== "Enter" || inputValue.trim() === "") {
        return;
      }
      const inputValueTrimmed = inputValue.trim().toLowerCase();
      const existingMember = findMemberByEmail(inputValueTrimmed);
      if (!existingMember && !canInviteByEmail) {
        return;
      }
      if (existingMember) {
        onOptionChange(existingMember);
      } else {
        addUserIfValidEmail(inputValueTrimmed);
        if (event.target instanceof HTMLElement) {
          event.target.blur();
        }
      }
      setInputValue("");
    };
    $[25] = addUserIfValidEmail;
    $[26] = canInviteByEmail;
    $[27] = findMemberByEmail;
    $[28] = inputValue;
    $[29] = onOptionChange;
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  const handleKeyPress = t13;
  let t14;
  if ($[31] !== addUserIfValidEmail || $[32] !== findMemberByEmail || $[33] !== inputValue) {
    t14 = function onBlur() {
      if (!inputValue) {
        return;
      }
      const inputValueTrimmed_0 = inputValue.trim().toLowerCase();
      if (!findMemberByEmail(inputValueTrimmed_0)) {
        addUserIfValidEmail(inputValueTrimmed_0);
      }
    };
    $[31] = addUserIfValidEmail;
    $[32] = findMemberByEmail;
    $[33] = inputValue;
    $[34] = t14;
  } else {
    t14 = $[34];
  }
  const onBlur = t14;
  let t15;
  if ($[35] !== mapMembersToOptions || $[36] !== onOptionChange) {
    t15 = function clearSelection() {
      onOptionChange(null);
      setInputValue("");
      setCurrentOptions(mapMembersToOptions());
    };
    $[35] = mapMembersToOptions;
    $[36] = onOptionChange;
    $[37] = t15;
  } else {
    t15 = $[37];
  }
  const clearSelection = t15;
  const noMembersFoundText = canInviteByEmail ? "No members found. Please type a valid email to invite a new member to the workspace" : "No members found";
  let t16;
  if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = value => setInputValue(value);
    $[38] = t16;
  } else {
    t16 = $[38];
  }
  let t17;
  if ($[39] !== shouldShowAllOptions) {
    t17 = (options, input) => {
      const lowerInputValue = input.toLowerCase();
      const filteredOptions = options.filter(option => option.title.toLowerCase().includes(lowerInputValue) || option.subtitle?.toLowerCase().includes(lowerInputValue));
      return shouldShowAllOptions ? filteredOptions : filteredOptions.slice(0, MAX_ITEMS_TO_DISPLAY);
    };
    $[39] = shouldShowAllOptions;
    $[40] = t17;
  } else {
    t17 = $[40];
  }
  let t18;
  if ($[41] !== clearSelection || $[42] !== isDisabled || $[43] !== placeholder || $[44] !== selectionDisplay) {
    t18 = () => <MemberAutocompleteSelection {...selectionDisplay} isDisabled={isDisabled} placeholder={placeholder} onClick={clearSelection} />;
    $[41] = clearSelection;
    $[42] = isDisabled;
    $[43] = placeholder;
    $[44] = selectionDisplay;
    $[45] = t18;
  } else {
    t18 = $[45];
  }
  let t19;
  if ($[46] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = {
      "& .MuiOutlinedInput-root": {
        minHeight: SELECTION_HEIGHT,
        paddingY: 1.25
      }
    };
    $[46] = t19;
  } else {
    t19 = $[46];
  }
  let t20;
  if ($[47] !== inputValue || $[48] !== isValidNewEmail || $[49] !== noMembersFoundText) {
    t20 = isValidNewEmail ? <Box component="div" sx={{
      overflow: "hidden",
      textOverflow: "ellipsis",
      wordBreak: "break-word",
      lineHeight: "24px",
      display: "-webkit-box",
      WebkitLineClamp: "3",
      WebkitBoxOrient: "vertical"
    }}>Type enter to invite{" "}<Box component="span" sx={{
        fontFamily: DEFAULT_FONT_FAMILY_ITALIC
      }}><var>{inputValue}</var></Box>{" "}to the workspace.</Box> : noMembersFoundText;
    $[47] = inputValue;
    $[48] = isValidNewEmail;
    $[49] = noMembersFoundText;
    $[50] = t20;
  } else {
    t20 = $[50];
  }
  let t21;
  if ($[51] !== t20) {
    t21 = <Box component="div">{t20}</Box>;
    $[51] = t20;
    $[52] = t21;
  } else {
    t21 = $[52];
  }
  let t22;
  if ($[53] !== currentOptions || $[54] !== handleKeyPress || $[55] !== inputValue || $[56] !== isDisabled || $[57] !== onBlur || $[58] !== onSelectionChange || $[59] !== placeholder || $[60] !== selectedValue || $[61] !== t17 || $[62] !== t18 || $[63] !== t21) {
    t22 = <SingleSelectAutocomplete placeholder={placeholder} isDisabled={isDisabled} value={selectedValue} options={currentOptions} inputValue={inputValue} onInputChange={t16} onKeyDown={handleKeyPress} onBlur={onBlur} onChange={onSelectionChange} filterOptions={t17} renderSelection={t18} inputSx={t19} noOptionsText={t21} />;
    $[53] = currentOptions;
    $[54] = handleKeyPress;
    $[55] = inputValue;
    $[56] = isDisabled;
    $[57] = onBlur;
    $[58] = onSelectionChange;
    $[59] = placeholder;
    $[60] = selectedValue;
    $[61] = t17;
    $[62] = t18;
    $[63] = t21;
    $[64] = t22;
  } else {
    t22 = $[64];
  }
  let t23;
  if ($[65] !== selectedMember) {
    t23 = typeof selectedMember === "string" && <Box component="div" sx={{
      fontSize: "12px",
      color: "text.primary",
      marginLeft: "4px",
      marginTop: "2px"
    }}>Inviting members by email will also invite them to the workspace</Box>;
    $[65] = selectedMember;
    $[66] = t23;
  } else {
    t23 = $[66];
  }
  let t24;
  if ($[67] !== t22 || $[68] !== t23) {
    t24 = <>{t22}{t23}</>;
    $[67] = t22;
    $[68] = t23;
    $[69] = t24;
  } else {
    t24 = $[69];
  }
  return t24;
}